// Phase H profile photo upload helper. Wraps the two-step flow:
//   1. POST /event-images/profile-upload-url → presigned PUT URL + s3Key
//   2. PUT the file directly to S3
// The caller is then responsible for calling the graphql
// setUserProfileImage mutation with the returned s3Key to persist the
// pointer onto the socialUsers row. That second step lives at the
// component layer because Apollo is wired in there; this module only
// handles the REST + S3 plumbing.

import { authFetch } from "./authFetch";

const BASE_URL =
  process.env.REACT_APP_EVENT_IMAGES_API_URL ||
  "https://7vnhfarqxg.execute-api.us-east-1.amazonaws.com/prod";

const trimmedBase = () => BASE_URL.replace(/\/$/, "");

const safeJson = async (response) => {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
};

const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

const validate = (file, targetUserId) => {
  if (!file) throw new Error("file is required");
  if (!targetUserId) throw new Error("targetUserId is required");
  if (file.type === "image/heic" || file.type === "image/heif") {
    throw new Error(
      "HEIC images aren't supported — convert to JPEG/PNG/WEBP first."
    );
  }
  if (!ALLOWED.includes(file.type)) {
    throw new Error(`Photo must be JPEG, PNG, or WEBP (got ${file.type}).`);
  }
  if (file.size > MAX_BYTES) {
    throw new Error(`Photo is larger than 10 MB (got ${(file.size / 1024 / 1024).toFixed(1)} MB).`);
  }
};

/**
 * Upload a profile photo to S3. Returns the s3Key on success.
 * The caller should then call the graphql setUserProfileImage
 * mutation with this key so the socialUsers row points at it.
 *
 * @param {{ file: File, targetUserId: string }} params
 * @returns {Promise<{ s3Key: string }>}
 */
export const uploadProfilePhoto = async ({ file, targetUserId }) => {
  validate(file, targetUserId);

  const presignResp = await authFetch(
    `${trimmedBase()}/event-images/profile-upload-url`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        contentType: file.type,
        sizeBytes: file.size,
        targetUserId,
      }),
    }
  );
  if (!presignResp.ok) {
    const data = await safeJson(presignResp);
    throw new Error(
      (data && data.error) ||
        `Failed to request profile upload URL (${presignResp.status})`
    );
  }
  const { uploadUrl, s3Key } = await presignResp.json();

  const putResp = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!putResp.ok) {
    throw new Error(`S3 upload failed (${putResp.status})`);
  }

  return { s3Key };
};
